
// --- Vue & Template imports ---
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// Models
import { MenuItem, Menu } from '@/models/Menu.model';

// --- Third Party imports ---
import gsap from 'gsap';
import colors from 'vuetify/lib/util/colors';

@Component
export default class PageCard extends Vue {
  @Prop(Object) readonly topMenuItems!: Menu;

  previousTarget: HTMLElement | boolean = false;
  storeTextRegex: RegExp = /.[^\s]\/.[^\s]/;

  menuItemClicked(event: Event, clickedMenuItem: MenuItem) {
    if (
      (typeof this.getMenuDisabledState(clickedMenuItem) === 'boolean' &&
        this.getMenuDisabledState(clickedMenuItem)) ||
      clickedMenuItem.divider
    ) {
      event.stopPropagation();
      return; // consume event so the tooltip can work
    }
    if (clickedMenuItem.customEvent) {
      this.$root.$emit(clickedMenuItem.customEvent);
      return; // ??? should this prevent routes etc ???
    }

    if (clickedMenuItem.routeName) {
      this.redirect(clickedMenuItem.routeName);
    }

    this.menuActive(event, clickedMenuItem);
  }

  menuItemEnter(event: Event, clickedMenuItem: MenuItem) {
    if (clickedMenuItem.children && clickedMenuItem.childrenToggle) {
      if (event.target) {
        const item = event.currentTarget as HTMLElement;
        const sibling = item.querySelector('.js-component-top-menu-children');

        if (sibling) {
          gsap.set(sibling, { height: 'auto' });
          gsap.from(sibling, {
            duration: 0.1,
            height: 0,
            onStart: () => {
              item.classList.add('state--open');
              this.previousTarget = item;
            },
          });
        }
      }
    }
  }

  menuItemLeave(event: Event, clickedMenuItem: MenuItem) {
    if (clickedMenuItem.children && clickedMenuItem.childrenToggle) {
      if (event.target) {
        const item = event.currentTarget as HTMLElement;
        const sibling = item.querySelector('.js-component-top-menu-children');

        if (sibling) {
          gsap.to(sibling, {
            duration: 0.1,
            height: 0,
            onStart: () => {
              item.classList.remove('state--open');
            },
          });
        }
      }
    }
  }

  menuActive(event: Event, clickedMenuItem: MenuItem) {
    if (this.previousTarget) {
      const previousTarget: HTMLElement = this.previousTarget as HTMLElement;
      const previousTargetSibling = (
        this.previousTarget as HTMLElement
      ).querySelector('.js-component-top-menu-children');

      if (previousTarget) {
        gsap.to(previousTargetSibling, {
          duration: 0.1,
          height: 0,
          onStart: () => {
            previousTarget.classList.remove('state--open');
            this.previousTarget = false;
          },
        });
      }
    }

    if (clickedMenuItem.children && clickedMenuItem.childrenToggle) {
      if (event.target) {
        const item = event.currentTarget as HTMLElement;
        const sibling = item.querySelector('.js-component-top-menu-children');

        if (sibling) {
          if (item.classList.contains('state--open')) {
            gsap.to(sibling, {
              duration: 0.1,
              height: 0,
              onStart: () => {
                item.classList.remove('state--open');
              },
            });
          } else {
            gsap.set(sibling, { height: 'auto' });
            gsap.from(sibling, {
              duration: 0.1,
              height: 0,
              onStart: () => {
                item.classList.add('state--open');
                this.previousTarget = item;
              },
            });
          }
        }
      }
    }
  }

  closeMenus() {
    if (this.previousTarget) {
      const previousTarget: HTMLElement = this.previousTarget as HTMLElement;
      const previousTargetSibling = (
        this.previousTarget as HTMLElement
      ).querySelector('.js-component-top-menu-children');

      if (previousTarget) {
        gsap.to(previousTargetSibling, {
          duration: 0.1,
          height: 0,
          onStart: () => {
            previousTarget.classList.remove('state--open');
            this.previousTarget = false;
          },
        });
      }
    }
  }

  redirect(route: string) {
    // check if current route is the route that the menu is going to, ignore if it is
    if (this.$route.name === route) {
      return;
    }

    this.closeMenus();
    this.$router.push({ name: route });
  }

  getMenuDisabledState(menuItem: MenuItem) {
    if (typeof menuItem.disabled === 'string') {
      return this.$store.getters[menuItem.disabled];
    }

    return false;
  }

  getMenuDisabledTooltip(menuItem: MenuItem) {
    if (typeof menuItem.disabledTooltip === 'string') {
      return menuItem.disabledTooltip.match(this.storeTextRegex)
        ? this.$store.getters[menuItem.disabledTooltip]
        : menuItem.disabledTooltip;
    }

    return false;
  }

  getColor(colorString: string) {
    if (colorString.indexOf('#') > -1) {
      return colorString;
    }

    if (this.$vuetify.theme.currentTheme) {
      for (const [colorIndex, color] of Object.entries(
        this.$vuetify.theme.currentTheme,
      )) {
        if (colorString === colorIndex) {
          return color + '';
        }
      }
    }

    if (colors) {
      for (const [colorIndex, color] of Object.entries(colors)) {
        if (colorString === colorIndex) {
          return color.base + '';
        }
      }
    }

    return colorString;
  }

  getTheme() {
    if (this.topMenuItems && this.topMenuItems.theme) {
      let theme = {};

      for (const [colorKey, color] of Object.entries(this.topMenuItems.theme)) {
        theme = {
          ...theme,
          ...{ ['--' + colorKey]: this.getColor(color + '') },
        };
      }

      return theme;
    }
    return {};
  }
}
